import type { SaveOrShareSquadRequestData } from "@/api/privateRequests/squad-builder/save-squad";
import { SquadBuilderPlayer } from "@/components/molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import ArrowButton from "@/design-system/ArrowButton/ArrowButton";
import { Button } from "@/design-system/Button/Button";
import { arrow } from "@/designSystemIcons";
import { findMetaRating } from "@/redux/squadBuilder/findMetaRating";
import type { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { IRootState } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import type { ObjectiveSquadBlock } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

type ObjectiveSquad = Extract<
  ObjectiveSquadBlock,
  { status: "done" }
>["squads"][number];

const Card = ({
  player,
  position,
  tabletOrLargerMode,
  desktopMode,
  bench,
}: {
  player: SquadBuilderPlayerType;
  position: string;
  tabletOrLargerMode: boolean;
  desktopMode: boolean;
  bench?: boolean;
}) => {
  const metaRating = (chemistry: number) => {
    try {
      return (
        findMetaRating({
          metaRatings: player.metaRatings,
          playerRoleId: player.playerRoleId,
          chemistry,
        })?.metaRating ?? 0
      );
    } catch {
      return 0;
    }
  };
  return (
    <SquadBuilderPlayer
      player={player}
      position={position}
      formationPosition={position}
      playerRoleId={player.playerRoleId}
      metaRating={metaRating(player.chemistry ?? 0)}
      metaRatingZeroChem={metaRating(0)}
      isOnPitch={!bench}
      isOnBench={bench}
      tabletOrLargerMode={tabletOrLargerMode}
      desktopMode={desktopMode}
    />
  );
};

/** The saved-squads request for a built objective squad. */
export const objectiveSquadSaveRequest = (
  squad: ObjectiveSquad,
  name: string,
  squadId?: string,
): SaveOrShareSquadRequestData => ({
  squadId,
  name,
  formation: squad.tactic.teamTactics.formationId,
  players: (squad.players as unknown as SquadBuilderPlayerType[]).map((p) => ({
    playerRoleId: p.playerRoleId,
    resourceId: p.resourceId,
    evoPathId: p.evoPathId,
  })),
  manager: {
    countryId: squad.manager.country ?? undefined,
    leagueId: squad.manager.league ?? undefined,
    eaId: squad.manager.ea_id,
  },
  tactic: {
    name: squad.tactic.name,
    description: squad.tactic.description ?? "",
    logoUrl: squad.tactic.logoUrl,
    teamTactics: squad.tactic.teamTactics,
    playerRoles: squad.tactic.playerRoles.map((r) => ({
      playerRoleId: r.playerRoleId,
      focus: r.focus,
    })),
  },
});

/**
 * A block's built squads, read-only: the XI on the pitch at the formation's
 * coordinates, laid out like the squad builder's pitch. Only Gauntlet blocks
 * hold several squads; those cycle and show the bench, as no card may repeat
 * across them.
 */
export const ObjectiveSquadView = ({
  squads,
  initialIndex,
  onClose,
}: {
  squads: ObjectiveSquad[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation("objectives");
  const [index, setIndex] = useState(initialIndex);
  const squad = squads[index]!;
  const gauntlet = squads.length > 1;
  const formationId = squad.tactic.teamTactics.formationId;
  const formation = useSelector(
    (state: IRootState) => state.loadedContentReducer.formations[formationId],
  );
  const players = squad.players as unknown as SquadBuilderPlayerType[];
  const desktopMode = !useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const tabletOrLargerMode = !useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <div className="flex flex-col gap-y-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col">
          <span className="text-base font-semibold text-foreground">
            {squad.tactic.name}
          </span>
          <span className="text-sm text-foreground-muted">
            {[
              formation?.label ?? formationId,
              t("squad.rating", { rating: squad.rating }),
              t("squad.chemistry", { chemistry: squad.chemistry }),
            ].join(" · ")}
          </span>
        </div>
        <div className="flex items-center gap-x-3">
          {gauntlet && (
            <div className="flex items-center gap-x-2">
              <ArrowButton
                direction="left"
                disabled={index === 0}
                onClick={() => setIndex(index - 1)}
              />
              <span className="whitespace-nowrap text-sm text-foreground">
                {t("squad.cycle", { i: index + 1, n: squads.length })}
              </span>
              <ArrowButton
                direction="right"
                disabled={index === squads.length - 1}
                onClick={() => setIndex(index + 1)}
              />
            </div>
          )}
          {/* The minimize button other sheets' headers draw (SheetHeader). */}
          <Button
            onClick={onClose}
            aria-label={t("common:close")}
            variant="secondary"
            size="small"
            icon={<div className="size-3">{arrow}</div>}
          />
        </div>
      </div>
      <div className="mx-auto isolate w-full max-w-[950px] rounded-lg bg-gradient-to-b from-emerald-950 to-emerald-900 py-8">
        {/* The squad builder's player area: each card centred on its
            formation coordinates, nudged up as there. */}
        <div className="relative mx-auto h-[560px] w-[95%] mobile:h-[890px] mobile:w-[80%]">
          {formation?.pitchCoordinates.slice(0, 11).map(({ x, y }, i) => (
            <div
              key={i}
              className="absolute -translate-x-1/2 -translate-y-1/2 max-mobile:scale-90"
              style={{ left: `${x * 100}%`, top: `${y * 100 - 5.2}%` }}
            >
              <Card
                player={players[i]!}
                position={
                  formation.positions[i] ?? players[i]!.preferredPosition
                }
                tabletOrLargerMode={tabletOrLargerMode}
                desktopMode={desktopMode}
              />
            </div>
          ))}
        </div>
      </div>
      {gauntlet && (
        <div className="flex flex-col gap-y-2">
          <span className="text-sm font-semibold text-foreground">
            {t("squad.bench")}
          </span>
          <div className="flex gap-x-2 overflow-x-auto pb-2">
            {players.slice(11).map((player, i) => (
              <div key={i} className="shrink-0">
                <Card
                  player={player}
                  position={
                    player.possiblePositions?.includes(player.position)
                      ? player.position
                      : player.preferredPosition
                  }
                  tabletOrLargerMode={tabletOrLargerMode}
                  desktopMode={desktopMode}
                  bench
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
